import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { core } from '@musinsa/pbo-core'
import { debounce } from 'lodash-es'
import { gridColumnsQueryKeys, useGetGridColumns } from '@/services/auth/useAuthQueries'
import { callToSaveGridColumns } from '@/services/auth/apis'
import { GridColumnState } from '@/services/auth/gridColumn.types'
import { GRID_COLUMNS_SAVE_DEBOUNCE_MS } from '@/services/auth/consts'
import { AuthStorage } from '@/utils/storage/AuthStorage'

type Pin = 'left' | 'right' | 'none'
type ColumnSpec = { dataIndex?: unknown; fixed?: unknown }
type Layout = { order: string[]; widths: Record<string, number>; pinned: Record<string, Pin> }

export type CoreTableLayout = Pick<
  core.CoreTableProps,
  'columnOrder' | 'onColumnOrderChange' | 'columnWidths' | 'onColumnWidthsChange' | 'pinnedColumns' | 'onPinnedColumnsChange' | 'hiddenColumns'
>

const NO_LEGACY_COLUMNS: GridColumnState[] = []

// core.Table 과 같은 규칙 — 정의의 fixed 가 기본 고정이다.
const definedPin = (col: ColumnSpec): Pin => (col.fixed === true || col.fixed === 'left' ? 'left' : col.fixed === 'right' ? 'right' : 'none')

/** saved 순서대로 두고, 화면에 없는 colId 는 버리고, saved 에 없는 컬럼은 정의 순서로 뒤에 붙인다. */
const orderOf = (ids: string[], saved: string[]) => [...saved.filter((id) => ids.includes(id)), ...ids.filter((id) => !saved.includes(id))]

/**
 *  @hook useCoreTableLayout
 *  - core.Table 의 개인 컬럼 배치(순서·너비·고정)를 사용자·화주별로 서버에서 읽어 제어형 prop 으로 주고, 사용자가 바꾸면 서버에 저장한다.
 *  - 표 콜백은 바뀐 값만 준다(순서는 드래그 때만). 저장은 배열 순서 = 컬럼 순서라, columns(표에 넘기는 정의)로 전체 컬럼을 채워 저장한다 —
 *    너비만 바꾼 컬럼이 새로고침 뒤 맨 앞으로 가지 않게.
 *  - 저장값 pinned null 은 'none' 으로 준다 — 정의에 fixed 가 있는 컬럼을 푼 것도 복원된다.
 *  - 숨김은 저장 형식에 칸이 없어 다루지 않는다(표 내부 상태). 다만 저장값을 읽는 동안은 컬럼을 모두 숨긴다 — 기본 배치로 그렸다가 저장 배치로 다시 그리는 깜빡임을 막는다.
 */
export const useCoreTableLayout = (gridKey: string, columns: readonly ColumnSpec[]): CoreTableLayout => {
  // 화주 전환은 페이지를 새로 읽으므로(MainHeader onConvert) 마운트 동안 바뀌지 않는다.
  const shipperCode = useMemo(() => AuthStorage.user.get().shipperCode ?? '', [])
  const queryClient = useQueryClient()
  const { data: serverState, isSuccess: loaded, isPending, fetchStatus } = useGetGridColumns(gridKey, shipperCode, NO_LEGACY_COLUMNS)
  // 조회 중(오프라인 대기 포함). 화주가 없어 조회하지 않는 경우(fetchStatus idle)는 아니다.
  const loading = isPending && fetchStatus !== 'idle'

  // 화면에서 바꾼 값 — 저장 응답을 기다리지 않고 바로 반영한다.
  const [localState, setLocalState] = useState<GridColumnState[]>()

  const putQueue = useRef<Promise<unknown>>(Promise.resolve())
  const save = useMemo(
    () =>
      debounce((items: GridColumnState[]) => {
        if (!shipperCode) return
        putQueue.current = putQueue.current.then(() => callToSaveGridColumns(gridKey, shipperCode, items)).catch(() => undefined)
      }, GRID_COLUMNS_SAVE_DEBOUNCE_MS),
    [gridKey, shipperCode],
  )

  useEffect(() => () => save.flush(), [save])

  const specs = columns.flatMap((col) => (typeof col.dataIndex === 'string' ? [{ colId: col.dataIndex, pin: definedPin(col) }] : []))
  const ids = specs.map((spec) => spec.colId)
  const saved = (localState ?? serverState ?? []).filter((state) => ids.includes(state.colId))

  const layout: Layout = {
    order: orderOf(
      ids,
      saved.map((state) => state.colId),
    ),
    widths: Object.fromEntries(saved.flatMap((state) => (state.width === null ? [] : [[state.colId, state.width]]))),
    pinned: Object.fromEntries(saved.map((state) => [state.colId, state.pinned ?? 'none'])),
  }

  // 고정 영역을 넘나드는 드래그는 순서·고정 콜백을 연달아 부른다 — 리렌더 전이라 두 번째 콜백이 첫 번째 결과를 읽게 ref 로 잇는다.
  const latest = useRef(layout)
  latest.current = layout

  const update = (patch: Partial<Layout>) => {
    // 조회 중에는 바꾼 값을 받지 않는다 — 기본 배치로 만든 localState 가 늦게 온 저장값을 가리고 다음 저장에서 덮는다.
    if (loading) return
    const next = { ...latest.current, ...patch }
    latest.current = next

    const items = next.order.map((colId) => {
      const pin = next.pinned[colId] ?? specs.find((spec) => spec.colId === colId)?.pin ?? 'none'
      return { colId, width: next.widths[colId] ?? null, pinned: pin === 'none' ? null : pin }
    })

    setLocalState(items)
    // 조회가 실패했으면(또는 화주가 없어 조회하지 않았으면) 화면에만 반영한다 — 기본 배치 기준 전체 저장이 서버의 기존 배치를 덮지 않게.
    if (!loaded) return
    queryClient.setQueryData(gridColumnsQueryKeys.detail(gridKey, shipperCode), items)
    save(items)
  }

  return {
    columnOrder: layout.order,
    columnWidths: layout.widths,
    pinnedColumns: layout.pinned,
    // 조회가 끝나면 undefined — 표 내부 숨김 상태로 돌아간다
    hiddenColumns: loading ? ids : undefined,
    onColumnOrderChange: (order) => update({ order: orderOf(ids, order) }),
    onColumnWidthsChange: (widths) => update({ widths }),
    onPinnedColumnsChange: (pinned) => update({ pinned }),
  }
}

/** 생성본 wiring.columnLayout 슬롯 — `columnLayout: { rgr01Table: coreTableLayoutSlot('RGR16_HEAD') }`. gridKey 는 BE 상한 10자. */
export const coreTableLayoutSlot = (gridKey: string) => (columns: readonly ColumnSpec[]) =>
  // eslint-disable-next-line react-hooks/rules-of-hooks -- 생성본이 렌더 본문 맨 위에서 매번 같은 순서로 부른다(page.generated.tsx 슬롯 호출 줄)
  useCoreTableLayout(gridKey, columns)
